import { useEffect, useState, useRef } from 'react';
import {
  Save, User, Check, AlertCircle, Camera, FileText,
  Shield, Trash2, Upload, Eye, ChevronDown, ChevronUp,
  CheckCircle, Clock, X,
} from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { dealerUpdateProfile } from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600 rounded-none";
const labelCls = "block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-1.5";

const DOC_TYPES = [
  { key: 'ffl',   label: 'FFL License Card',    icon: Shield,   color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5',  hint: 'Front of your Federal Firearms License' },
  { key: 'sot',   label: 'SOT Registration',    icon: FileText, color: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/5', hint: 'Special Occupational Taxpayer form' },
  { key: 'other', label: 'Other Credential',    icon: Upload,   color: 'text-blue-400',   border: 'border-blue-500/30',   bg: 'bg-blue-500/5',   hint: 'Business license, state permit, etc.' },
];

// ── Document Upload Card ───────────────────────────────────────────────────────
function DocUploadCard({ docType, existingDocs, token, onUploaded, onDeleted }) {
  const cfg = DOC_TYPES.find(d => d.key === docType.key);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const Icon = cfg.icon;

  const myDocs = existingDocs.filter(d => d.document_type === docType.key);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    setProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType.key);
      formData.append('notes', '');

      // Simulate progress
      const progInterval = setInterval(() => setProgress(p => Math.min(p + 15, 85)), 300);

      const res = await fetch(`${BASE}/api/dealer/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      clearInterval(progInterval);
      setProgress(100);

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }

      const doc = await res.json();
      onUploaded(doc);
      setTimeout(() => setProgress(0), 1000);
    } catch (err) {
      setUploadError(err.message);
      setProgress(0);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await fetch(`${BASE}/api/dealer/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      onDeleted(docId);
    } catch (e) {
      alert(e.message);
    }
  };

  const isImage = (name) => /\.(jpg|jpeg|png|webp|heic)$/i.test(name || '');

  return (
    <div className={`border ${cfg.border} ${cfg.bg} overflow-hidden`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <Icon size={16} className={cfg.color} />
        <div className="flex-1">
          <p className={`font-bebas text-base tracking-wider ${cfg.color}`}>{cfg.label}</p>
          <p className="font-inter text-[10px] text-zinc-600">{cfg.hint}</p>
        </div>
        {myDocs.length > 0 && (
          <span className="flex items-center gap-1 font-inter text-[10px] text-green-400 border border-green-500/30 bg-green-500/10 px-2 py-0.5">
            <CheckCircle size={9} />{myDocs.length} on file
          </span>
        )}
      </div>

      {/* Existing documents */}
      {myDocs.length > 0 && (
        <div className="divide-y divide-white/5">
          {myDocs.map(doc => (
            <div key={doc.doc_id} className="flex items-center gap-3 px-4 py-3">
              {/* Thumbnail or icon */}
              <div className="w-10 h-10 bg-[#111] border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {isImage(doc.filename) ? (
                  <img
                    src={`${BASE}${doc.url}`}
                    alt={doc.document_label}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <FileText size={16} className={cfg.color} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-inter text-xs text-white truncate">{doc.original_name}</p>
                <p className="font-inter text-[10px] text-zinc-600">
                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  {doc.size_bytes ? ` · ${(doc.size_bytes / 1024).toFixed(0)}KB` : ''}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a
                  href={`${BASE}${doc.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-zinc-500 hover:text-white transition-colors"
                  title="View document"
                >
                  <Eye size={14} />
                </a>
                <button
                  onClick={() => handleDelete(doc.doc_id)}
                  className="p-2 text-zinc-700 hover:text-red-400 transition-colors"
                  title="Remove document"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      <div className="px-4 py-4">
        {uploadError && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-xs px-3 py-2 mb-3">
            <AlertCircle size={12} />{uploadError}
          </div>
        )}

        {/* Progress bar */}
        {uploading && progress > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-inter text-[10px] text-zinc-500">Uploading...</span>
              <span className="font-inter text-[10px] text-zinc-500">{progress}%</span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Hidden file input — capture="environment" opens camera on mobile */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.heic"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`w-full flex items-center justify-center gap-3 py-4 border-2 border-dashed transition-all ${
            uploading
              ? 'border-zinc-800 text-zinc-700 cursor-not-allowed'
              : `${cfg.border.replace('border-', 'border-')} hover:${cfg.bg.replace('/5', '/10')} ${cfg.color} cursor-pointer active:scale-95`
          }`}
        >
          {uploading ? (
            <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />Uploading...</>
          ) : (
            <>
              <Camera size={20} className={cfg.color} />
              <div className="text-left">
                <p className={`font-bebas text-base tracking-wider ${cfg.color}`}>
                  {myDocs.length > 0 ? 'ADD ANOTHER' : 'UPLOAD / TAKE PHOTO'}
                </p>
                <p className="font-inter text-[10px] text-zinc-600">
                  Tap to open camera or choose file · PDF, JPG, PNG
                </p>
              </div>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main Profile Page ─────────────────────────────────────────────────────────
export default function DealerProfile() {
  const { dealer, token, refreshDealer } = useDealerAuth();
  const [form, setForm] = useState({
    business_address: '', city: '', state: '', zip_code: '',
    tax_id: '', ffl_number: '', ffl_expiry: '', sot_status: 'no',
  });
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [profileOpen, setProfileOpen] = useState(true);
  const [docsOpen, setDocsOpen] = useState(true);

  useEffect(() => {
    if (dealer) {
      setForm({
        business_address: dealer.business_address || '',
        city: dealer.city || '', state: dealer.state || '',
        zip_code: dealer.zip_code || '', tax_id: dealer.tax_id || '',
        ffl_number: dealer.ffl_number || '', ffl_expiry: dealer.ffl_expiry || '',
        sot_status: dealer.sot_status || 'no',
      });
    }
    // Load documents
    if (token) {
      fetch(`${BASE}/api/dealer/documents`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(docs => setDocuments(Array.isArray(docs) ? docs : []))
        .catch(() => setDocuments([]));
    }
  }, [dealer, token]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSuccess(''); setError('');
    try {
      await dealerUpdateProfile(token, form);
      await refreshDealer();
      setSuccess('Profile saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDocUploaded = (doc) => {
    setDocuments(prev => [...prev, doc]);
  };

  const handleDocDeleted = (docId) => {
    setDocuments(prev => prev.filter(d => d.doc_id !== docId));
  };

  // FFL status indicators
  const fflOnFile = !!form.ffl_number || documents.some(d => d.document_type === 'ffl');
  const sotOnFile = form.sot_status === 'yes' || documents.some(d => d.document_type === 'sot');

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="dealer-profile-page">

      {/* Page header */}
      <div className="mb-6 px-1">
        <h1 className="font-bebas text-3xl sm:text-4xl text-white tracking-wider">MY ACCOUNT</h1>
        <p className="font-inter text-sm text-zinc-500 mt-1">Manage your dealer profile and upload credentials</p>
      </div>

      {/* Quick compliance status */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className={`p-4 border flex items-center gap-3 ${fflOnFile ? 'border-green-500/30 bg-green-500/5' : 'border-zinc-800 bg-[#1a1a1a]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${fflOnFile ? 'bg-green-500' : 'bg-zinc-800'}`}>
            {fflOnFile ? <Check size={16} className="text-white" /> : <Shield size={14} className="text-zinc-600" />}
          </div>
          <div>
            <p className={`font-bebas text-sm tracking-wider ${fflOnFile ? 'text-green-400' : 'text-zinc-600'}`}>FFL LICENSE</p>
            <p className="font-inter text-[10px] text-zinc-600">{fflOnFile ? 'On file' : 'Not uploaded'}</p>
          </div>
        </div>
        <div className={`p-4 border flex items-center gap-3 ${sotOnFile ? 'border-green-500/30 bg-green-500/5' : 'border-zinc-800 bg-[#1a1a1a]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${sotOnFile ? 'bg-green-500' : 'bg-zinc-800'}`}>
            {sotOnFile ? <Check size={16} className="text-white" /> : <FileText size={14} className="text-zinc-600" />}
          </div>
          <div>
            <p className={`font-bebas text-sm tracking-wider ${sotOnFile ? 'text-green-400' : 'text-zinc-600'}`}>SOT FORM</p>
            <p className="font-inter text-[10px] text-zinc-600">{sotOnFile ? 'Confirmed' : 'Not uploaded'}</p>
          </div>
        </div>
      </div>

      {/* ── CREDENTIALS UPLOAD (most important — shown prominently) ── */}
      <div className="bg-[#1a1a1a] border border-white/10 mb-5 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-4 text-left"
          onClick={() => setDocsOpen(o => !o)}
        >
          <div className="flex items-center gap-3">
            <Camera size={18} className="text-orange-500" />
            <div>
              <p className="font-bebas text-lg text-white tracking-wider">FFL &amp; SOT CREDENTIALS</p>
              <p className="font-inter text-[10px] text-zinc-500">Upload or photograph your license documents</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-inter text-[10px] text-zinc-600">{documents.length} file{documents.length !== 1 ? 's' : ''}</span>
            {docsOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </div>
        </button>

        {docsOpen && (
          <div className="border-t border-white/10 divide-y divide-white/5">
            {/* Mobile tip */}
            <div className="flex items-center gap-3 px-4 py-3 bg-orange-500/5 border-b border-orange-500/10">
              <Camera size={14} className="text-orange-400 flex-shrink-0" />
              <p className="font-inter text-xs text-orange-300">
                <strong>On your phone?</strong> Tap "Upload / Take Photo" to snap your FFL or SOT card directly with your camera.
              </p>
            </div>

            <div className="p-4 space-y-4">
              {DOC_TYPES.map(dt => (
                <DocUploadCard
                  key={dt.key}
                  docType={dt}
                  existingDocs={documents}
                  token={token}
                  onUploaded={handleDocUploaded}
                  onDeleted={handleDocDeleted}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── PROFILE FORM ── */}
      <div className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-4 text-left"
          onClick={() => setProfileOpen(o => !o)}
        >
          <div className="flex items-center gap-3">
            <User size={18} className="text-orange-500" />
            <div>
              <p className="font-bebas text-lg text-white tracking-wider">BUSINESS &amp; LICENSE DETAILS</p>
              <p className="font-inter text-[10px] text-zinc-500">Address, FFL number, SOT status</p>
            </div>
          </div>
          {profileOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
        </button>

        {profileOpen && (
          <div className="border-t border-white/10 p-4">
            {/* Read-only account info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 pb-5 border-b border-white/10">
              {[
                { label: 'Business Name', value: dealer?.business_name },
                { label: 'Contact', value: `${dealer?.first_name || ''} ${dealer?.last_name || ''}`.trim() },
                { label: 'Email', value: dealer?.email },
                { label: 'Account Status', value: dealer?.status?.replace('_', ' ').toUpperCase() },
              ].map(f => (
                <div key={f.label}>
                  <label className={labelCls}>{f.label}</label>
                  <div className="bg-[#111] border border-white/5 px-4 py-3 font-inter text-sm text-zinc-400">{f.value || '—'}</div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {error && <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3"><AlertCircle size={14} />{error}</div>}
              {success && <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3"><Check size={14} />{success}</div>}

              {/* Address */}
              <div>
                <label className={labelCls}>Business Address</label>
                <input className={inputCls} value={form.business_address} onChange={set('business_address')} placeholder="123 Range Road" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className={labelCls}>City</label>
                  <input className={inputCls} value={form.city} onChange={set('city')} placeholder="Austin" />
                </div>
                <div>
                  <label className={labelCls}>State</label>
                  <input className={inputCls} value={form.state} onChange={set('state')} placeholder="TX" maxLength={2} />
                </div>
                <div>
                  <label className={labelCls}>ZIP</label>
                  <input className={inputCls} value={form.zip_code} onChange={set('zip_code')} placeholder="78701" />
                </div>
              </div>

              {/* FFL + Tax */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tax ID / EIN</label>
                  <input className={inputCls} value={form.tax_id} onChange={set('tax_id')} placeholder="XX-XXXXXXX" />
                </div>
                <div>
                  <label className={labelCls}>FFL License Number</label>
                  <input className={inputCls} value={form.ffl_number} onChange={set('ffl_number')} placeholder="1-TX-XXX-XX-XX-XXXXX" />
                </div>
                <div>
                  <label className={labelCls}>FFL Expiration</label>
                  <input type="date" className={inputCls} value={form.ffl_expiry} onChange={set('ffl_expiry')} />
                </div>
                <div>
                  <label className={labelCls}>SOT Status</label>
                  <select className={inputCls} value={form.sot_status} onChange={set('sot_status')}>
                    <option value="no">No SOT</option>
                    <option value="yes">Yes — SOT Holder (Class III)</option>
                  </select>
                </div>
              </div>

              <button
                data-testid="save-profile-button"
                type="submit"
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-4 transition-colors"
              >
                <Save size={16} />{saving ? 'SAVING...' : 'SAVE PROFILE'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
